import { useState } from "react";
import { Check, Snowflake } from "lucide-react";
import { BOOKING_URL } from "@/data/site";
import { LeadForm } from "@/components/LeadForm";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { Seo } from "@/components/Seo";

const INCLUDED = [
  "Two seasonal tune-ups per year (spring & fall)",
  "Free non-emergency diagnostic service call",
  "21-point system inspection each visit",
  "Priority scheduling over non-members",
  "Filter check and condensate drain flush",
  "Refrigerant level & electrical check",
  "Written system health report",
  "Reminder calls so you never miss a visit",
];

const PlanCheckoutCard = ({ payChoice, setPayChoice }) => (
  <div className="bg-white border-2 border-brand p-8 rounded-sm lg:sticky lg:top-32">
    <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand">Best Value</span>
    <h3 className="font-heading font-black text-2xl text-brand-ink mt-2">Choose how you pay</h3>
    <div className="mt-6 space-y-4">
      <div data-testid="plan-annual-option" className="border border-zinc-200 p-5 rounded-sm hover:border-brand transition-colors duration-200">
        <p className="text-sm text-zinc-500">Annual</p>
        <p><span className="font-heading font-black text-4xl text-brand">$249</span><span className="text-zinc-500"> /year</span></p>
        <p className="text-xs text-zinc-500 mt-1">Two months free vs. monthly</p>
      </div>
      <div data-testid="plan-monthly-option" className="border border-zinc-200 p-5 rounded-sm hover:border-brand transition-colors duration-200">
        <p className="text-sm text-zinc-500">Monthly</p>
        <p><span className="font-heading font-black text-4xl text-brand">$24.99</span><span className="text-zinc-500"> /month</span></p>
        <p className="text-xs text-zinc-500 mt-1">Easy budget billing</p>
      </div>
    </div>
    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="plan-join-btn"
      className="mt-6 block text-center bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-sm transition-colors duration-200">
      Join the Maintenance Plan
    </a>
    <p className="text-xs text-zinc-500 mt-3 text-center flex items-center justify-center gap-1">
      <Snowflake className="w-3 h-3" /> Secure enrollment via our PayPal booking page
    </p>
    <div className="mt-6 pt-6 border-t border-zinc-200">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Or enroll &amp; pay now</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button type="button" data-testid="pay-choice-annual" onClick={() => setPayChoice("maintenance-annual")}
          className={`font-bold py-2.5 rounded-sm text-sm border-2 transition-colors duration-200 ${payChoice === "maintenance-annual" ? "border-brand text-brand bg-brand-subtle" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}>
          $249 / year
        </button>
        <button type="button" data-testid="pay-choice-monthly" onClick={() => setPayChoice("maintenance-monthly")}
          className={`font-bold py-2.5 rounded-sm text-sm border-2 transition-colors duration-200 ${payChoice === "maintenance-monthly" ? "border-brand text-brand bg-brand-subtle" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}>
          $24.99 / month
        </button>
      </div>
      <PayPalCheckout itemId={payChoice} />
    </div>
  </div>
);

export default function MaintenancePlans() {
  const [payChoice, setPayChoice] = useState("maintenance-annual");
  return (
    <div data-testid="maintenance-plans-page">
      <Seo title="AC Maintenance Plans from $24.99/mo | Central Air Depot" description="Two seasonal tune-ups, a free non-emergency diagnostic and priority scheduling from $24.99/mo. Protect your AC year-round in South Florida." />
      <section className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-4 py-20 sm:py-28">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">Annual Residential Maintenance Plan</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-black max-w-3xl">
            Never sweat a breakdown again
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">
            Most AC failures in South Florida are preventable. Our plan catches small problems in spring and fall — before they become $1,500 emergencies in August.
          </p>
        </div>
      </section>

      <section className="bg-brand-subtle">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-8">What's included</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {INCLUDED.map((item) => (
                <div key={item} className="flex items-start gap-3 bg-white border border-zinc-200 p-4 rounded-sm">
                  <Check className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                  <span className="text-sm text-zinc-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <PlanCheckoutCard payChoice={payChoice} setPayChoice={setPayChoice} />
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-24">
          <LeadForm heading="Questions about the plan?" subtext="Ask us anything — we'll help you decide if it's right for your home." defaultService="Annual Maintenance Plan" />
        </div>
      </section>
    </div>
  );
}
